import React from 'react';
import styles from '../styles/StaticPages.module.css';
import { Helmet } from 'react-helmet';

const AboutUsPage = () => {
  return (
    <div className={styles.container}>
      <Helmet>
        <title>About MakeMeSongs | Unforgettable Personalized Song Gifts</title>
        <meta name="description" content="Transform your special moments into beautiful, custom-made songs. The perfect personalized gift for weddings, anniversaries, birthdays and other meaningful occasions." />
        <meta name="keywords" content="personalized music gifts, custom song presents, unique musical gifts, special occasion songs, memorable music gifts" />
        <meta property="og:title" content="About MakeMeSongs | Create Unforgettable Musical Gifts" />
        <meta property="og:description" content="Turn your memories and stories into beautiful personalized songs. The perfect heartfelt gift for weddings, birthdays, anniversaries and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com/about" />
        <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
        <link rel="canonical" href="https://makemesongs.com/about" />
      </Helmet>

      {/* Move the title outside the contentWrapper and add a subtitle */}
      <h1 className={styles.pageTitle}>About MakeMeSongs</h1>
      <p className={styles.introText}>
        Discover our journey and how we bring your stories to life through personalized music!
      </p>

      <div className={styles.contentWrapper}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Our Story</h2>
          <p>
            MakeMeSongs was founded with a simple mission: to make personalized music accessible to everyone.
            We believe that music has the unique power to capture emotions, celebrate special moments, and create lasting memories.
          </p>
          <p>
            What started as a passion project has evolved into an innovative service dedicated to crafting personalized 
            songs for life's most meaningful occasions. We harness modern technology and creative techniques to turn your 
            stories into beautiful musical expressions efficiently and affordably.
          </p>
        </section>
        
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>How It Works - Simple & Easy</h2>
          <p className={styles.sectionSubtitle}>Bringing Your Story to Life, One Note at a Time</p>
          <div className={styles.processList}>
            <div className={styles.processItem}>
              <div className={styles.processIcon}>1</div>
              <h3>Choose a Package</h3>
              <p>Select from our Basic, Premium, or Deluxe song packages.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processIcon}>2</div>
              <h3>Tell Your Story</h3>
              <p>Share the memories and details you want included in your song.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processIcon}>3</div>
              <h3>Select Music Style</h3>
              <p>Choose from a variety of musical genres and styles.</p>
            </div>
            <div className={styles.processItem}>
              <div className={styles.processIcon}>4</div>
              <h3>Receive Your Song</h3>
              <p>Within 24-48 hours, your custom song will be ready to download and share.</p>
            </div>
          </div>
          <p>
            Each step includes progress notifications via email to keep you informed throughout the process. Our online platform makes it easy to submit your information and preferences, allowing our team to create a truly personalized song experience.
          </p>
        </section>
        
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Our Approach</h2>
          <p>
            Behind MakeMeSongs is a passion for creating high-quality, personalized music using cutting-edge 
            production technology and creative approaches to songwriting.
          </p>
          <p>
            Our sophisticated creation process draws inspiration from a wide range of musical traditions and styles.
            This versatility allows us to create personalized songs in a variety of genres including pop, rock, country, 
            R&B, electronic, folk, jazz, hip-hop, and rap - tailored perfectly to your preferences.
          </p>
          <p>
            Each song features vocals crafted to perfectly match your chosen genre and style. We utilize advanced 
            production techniques to create professional-quality vocal performances that bring your story to life through music.
          </p>
        </section>
        
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Our Promise</h2>
          <p>
            At MakeMeSongs, we're committed to creating high-quality, personalized music that exceeds your expectations.
            We believe that every special moment deserves its own soundtrack, and we're honored to be part of your unique story.
          </p>
          <p>
            Your satisfaction is our priority. If you're not completely happy with your song, our Premium and Deluxe packages 
            include revision options to ensure your song is perfect. Premium packages include 1 revision, while Deluxe packages 
            include 2 revisions.
          </p>
          <p>
            We deliver your custom song as a high-quality MP3 file, with additional formats available depending on your package. 
            You receive full personal-use rights to your custom song - you can play it at events, share it with friends and family, 
            and use it in personal videos.
          </p>
        </section>
        
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>What Makes Us Different</h2>
          <p>
            Unlike generic music services, we focus exclusively on creating personalized songs that tell your unique story. 
            Our detailed questionnaire helps us capture the emotions, memories, and specific details that make your 
            relationship or occasion special.
          </p>
          <p>
            We understand that creating a meaningful custom song requires both creative innovation and careful attention to your 
            personal details. That's why we take the time to understand your story, preferences, and what makes your 
            occasion special before beginning the creation process.
          </p>
          <p>
            With quick turnaround times (24-48 hours for standard delivery, 3-6 hours for rush delivery), we ensure your 
            custom song is ready when you need it, without compromising on quality or personalization. Our innovative 
            approach allows us to deliver custom music more quickly and affordably than traditional methods.
          </p>
        </section>

        <div className={styles.ctaSection}>
          <h2>Ready to create your own personalized song?</h2>
          <a href="/packages" className={styles.ctaButton}>Get Started Today</a>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;